import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, query, where, orderBy, onSnapshot, or, doc, getDoc, limit } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { useAuth } from 'contexts/AuthContext';
import { useAssignments } from '../../hooks/useAssignments';
import { useClasses } from '../../hooks/useClasses';
import ConversationThread from './ConversationThread';
import NotificationCenter from './NotificationCenter';
import Icon from 'components/AppIcon';
import { toast } from 'sonner';

const MessageCenter = () => {
  const { user, profile } = useAuth();
  const [activeTab, setActiveTab] = useState('conversations');
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const userRole = profile?.is_admin ? 'admin' : profile?.is_writer ? 'writer' : profile?.is_qa ? 'qa' : 'client';

  // Function to fetch proper item title
  const fetchItemTitle = async (itemId, itemType) => {
    try {
      const docRef = doc(db, itemType === 'assignment' ? 'assignments' : 'class_management', itemId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return data.assignment_title || data.class_title || data.class_name || 'Untitled';
      }
      return 'Untitled';
    } catch (error) {
      console.error('Error fetching item title:', error);
      return 'Untitled';
    }
  };

  const { assignments } = useAssignments({ clientId: user?.uid });
  const { classes } = useClasses({ clientId: user?.uid });

  // Load conversations with optimized Firebase queries
  useEffect(() => {
    if (!user?.uid) return;

    // Get user's assignment and class IDs for filtering
    const userAssignmentIds = assignments?.map(a => a.id) || [];
    const userClassIds = classes?.map(c => c.id) || [];
    const userItemIds = [...userAssignmentIds, ...userClassIds];

    // Don't set up listeners if user has no items (prevents loading all data)
    if (userRole !== 'admin' && userItemIds.length === 0) {
      setConversations([]);
      setLoading(false);
      return;
    }

    const unsubscribes = [];

    // OPTIMIZED APPROACH: Different queries for different roles
    if (userRole === 'admin') {
      // Admin: Get recent messages with pagination
      const adminQuery = query(
        collection(db, 'comments'),
        orderBy('created_at', 'desc'),
        limit(100) // Limit for performance, can implement pagination
      );

      const adminUnsub = onSnapshot(adminQuery, async (snapshot) => {
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          created_at: doc.data().created_at?.toDate() || new Date()
        }));

        await processConversationData(data);
      });

      unsubscribes.push(adminUnsub);
    } else {
      // Client: Only get messages for their items (much more efficient)
      if (userItemIds.length > 0) {
        // Firebase 'in' operator supports up to 10 items, so we batch if needed
        const batches = [];
        for (let i = 0; i < userItemIds.length; i += 10) {
          batches.push(userItemIds.slice(i, i + 10));
        }

        batches.forEach(batch => {
          const clientQuery = query(
            collection(db, 'comments'),
            where('item_id', 'in', batch),
            orderBy('created_at', 'desc'),
            limit(50) // Limit for performance
          );

          const clientUnsub = onSnapshot(clientQuery, async (snapshot) => {
            const data = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              created_at: doc.data().created_at?.toDate() || new Date()
            }));

            await processConversationData(data, batch);
          });

          unsubscribes.push(clientUnsub);
        });
      }
    }

    // Process conversation data helper function
    async function processConversationData(data, batchItemIds = null) {
      // Enhance data with item titles where missing
      const enhancedData = await Promise.all(
        data.map(async (comment) => {
          if (!comment.item_title || comment.item_title === 'Untitled') {
            const title = await fetchItemTitle(comment.item_id, comment.item_type);
            return { ...comment, item_title: title };
          }
          return comment;
        })
      );

      // Update conversations state efficiently
      setConversations(currentConversations => {
        // Create a map of existing conversations for quick lookup
        const existingConversationsMap = new Map(
          currentConversations.map(conv => [`${conv.item_type}-${conv.item_id}`, conv])
        );

        // Group new/updated conversations
        const updatedConversations = enhancedData.reduce((acc, comment) => {
          const key = `${comment.item_type}-${comment.item_id}`;
          
          if (!acc[key]) {
            // Use existing conversation or create new one
            acc[key] = existingConversationsMap.get(key) || {
              id: key,
              item_id: comment.item_id,
              item_type: comment.item_type,
              item_title: comment.item_title || 'Untitled',
              last_message: comment,
              unread_count: 0,
              participants: new Set([comment.author_id]),
              messages: []
            };
          }
          
          // Update conversation with new message
          const existingMessageIndex = acc[key].messages.findIndex(msg => msg.id === comment.id);
          if (existingMessageIndex >= 0) {
            acc[key].messages[existingMessageIndex] = comment;
          } else {
            acc[key].messages.push(comment);
          }
          
          acc[key].participants.add(comment.author_id);
          
          // Update last message if this is newer
          if (comment.created_at > acc[key].last_message.created_at) {
            acc[key].last_message = comment;
          }
          
          if (comment.item_title && comment.item_title !== 'Untitled') {
            acc[key].item_title = comment.item_title;
          }
          
          return acc;
        }, {});

        // Calculate unread counts
        Object.values(updatedConversations).forEach(conversation => {
          conversation.unread_count = conversation.messages.filter(
            message => !message.read_by?.includes(user.uid) && message.author_id !== user.uid
          ).length;
        });

        // Merge with existing conversations that weren't in this batch
        const finalConversations = new Map();
        
        // Add updated conversations
        Object.entries(updatedConversations).forEach(([key, conv]) => {
          finalConversations.set(key, conv);
        });
        
        // Keep existing conversations that weren't updated (for batched queries)
        currentConversations.forEach(conv => {
          const key = `${conv.item_type}-${conv.item_id}`;
          if (!finalConversations.has(key)) {
            finalConversations.set(key, conv);
          }
        });

        const conversationsList = Array.from(finalConversations.values())
          .sort((a, b) => b.last_message.created_at - a.last_message.created_at);

        return conversationsList;
      });
      
      setLoading(false);
    }

    return () => unsubscribes.forEach(unsub => unsub());
  }, [user?.uid, userRole, assignments, classes]);

  // Filter conversations based on search
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    
    const query = searchQuery.toLowerCase();
    
    return conversations.filter(conv => {
      // Search in conversation title
      if (conv.item_title.toLowerCase().includes(query)) return true;
      
      // Search in all messages content and author names
      return conv.messages.some(message => 
        message.content.toLowerCase().includes(query) ||
        message.author_name.toLowerCase().includes(query)
      );
    });
  }, [conversations, searchQuery]);

  // Calculate total unread messages
  const totalUnreadMessages = useMemo(() => {
    return conversations.reduce((total, conv) => total + conv.unread_count, 0);
  }, [conversations]);

  // Auto-open conversation from session storage
  useEffect(() => {
    const openConversationData = sessionStorage.getItem('openConversation');
    if (openConversationData) {
      try {
        const { item_type, item_id, item_title } = JSON.parse(openConversationData);
        
        // First try to find existing conversation
        const conversation = conversations.find(conv => 
          conv.item_type === item_type && conv.item_id === item_id
        );
        
        if (conversation) {
          setSelectedConversation(conversation);
          setActiveTab('conversations');
          sessionStorage.removeItem('openConversation');
        } else if (conversations.length >= 0) {
          // Create a new conversation object if none exists
          const newConversation = {
            id: `${item_type}-${item_id}`,
            item_id: item_id,
            item_type: item_type,
            item_title: item_title || 'Untitled',
            last_message: null,
            unread_count: 0,
            participants: new Set([user.uid]),
            messages: []
          };
          
          setSelectedConversation(newConversation);
          setActiveTab('conversations');
          sessionStorage.removeItem('openConversation');
        }
      } catch (error) {
        console.error('Error parsing conversation data:', error);
        sessionStorage.removeItem('openConversation');
      }
    }
  }, [conversations, user]);

  const handleConversationSelect = (conversation) => {
    setSelectedConversation(conversation);
  };

  const getConversationIcon = (itemType) => {
    switch (itemType) {
      case 'assignment':
        return 'FileText';
      case 'class':
        return 'GraduationCap';
      default:
        return 'MessageCircle';
    }
  };

  const formatLastMessageTime = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Icon name="Loader2" size={32} className="text-white/60 animate-spin mb-2" />
          <p className="text-white/60">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col lg:flex-row">
      {/* Mobile Tab Navigation */}
      <div className="lg:hidden border-b border-white/10 bg-white/5">
        <div className="flex">
          <button
            onClick={() => setActiveTab('conversations')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
              activeTab === 'conversations'
                ? 'text-primary border-b-2 border-primary'
                : 'text-white/60 hover:text-white/80'
            }`}
          >
            <Icon name="MessageCircle" size={16} className="inline mr-2" />
            Messages
            {totalUnreadMessages > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {totalUnreadMessages > 99 ? '99+' : totalUnreadMessages}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'notifications'
                ? 'text-primary border-b-2 border-primary'
                : 'text-white/60 hover:text-white/80'
            }`}
          >
            <Icon name="Activity" size={16} className="inline mr-2" />
            Activity Feed
          </button>
        </div>
      </div>

      {/* Desktop Sidebar / Mobile Content */}
      <div className={`lg:w-1/3 lg:border-r lg:border-white/10 bg-white/5 ${
        activeTab === 'conversations' ? 'block' : 'hidden lg:block'
      }`}>
        {/* Search */}
        <div className="p-4 border-b border-white/10">
          <div className="relative">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="w-full pl-10 pr-10 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            )}
          </div>
          {searchQuery && (
            <div className="mt-2 text-xs text-white/60">
              {filteredConversations.length} conversation{filteredConversations.length !== 1 ? 's' : ''} found
            </div>
          )}
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="MessageCircle" size={48} className="text-white/20 mx-auto mb-2" />
              <p className="text-white/60">No conversations yet</p>
              <p className="text-white/40 text-sm">Messages will appear here</p>
            </div>
          ) : (
            <div className="space-y-1 p-2">
              {filteredConversations.map((conversation) => (
                <motion.div
                  key={conversation.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                    selectedConversation?.id === conversation.id
                      ? 'bg-primary/20 border border-primary/30'
                      : 'hover:bg-white/10 border border-transparent'
                  }`}
                  onClick={() => handleConversationSelect(conversation)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon 
                        name={getConversationIcon(conversation.item_type)} 
                        size={16} 
                        className="text-primary" 
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-white font-medium text-sm truncate">
                          {conversation.item_title}
                        </h3>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {conversation.unread_count > 0 && (
                            <span className="bg-primary text-white text-xs px-2 py-1 rounded-full">
                              {conversation.unread_count}
                            </span>
                          )}
                          <span className="text-white/40 text-xs">
                            {formatLastMessageTime(conversation.last_message.created_at)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className="text-white/60 text-xs">
                          {conversation.last_message.author_name}:
                        </span>
                        <p className="text-white/80 text-xs truncate">
                          {conversation.last_message.content}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-white/40 text-xs capitalize">
                          {conversation.item_type}
                        </span>
                        <span className="text-white/40 text-xs">•</span>
                        <span className="text-white/40 text-xs">
                          {conversation.participants.size} participant{conversation.participants.size !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {activeTab === 'conversations' && (
          <>
            {selectedConversation ? (
              <ConversationThread
                conversation={selectedConversation}
                onBack={() => setSelectedConversation(null)}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Icon name="MessageCircle" size={64} className="text-white/20 mx-auto mb-4" />
                  <h3 className="text-white font-medium mb-2">Select a conversation</h3>
                  <p className="text-white/60 text-sm">Choose a conversation from the sidebar to start messaging</p>
                </div>
              </div>
            )}
          </>
        )}
        
        {activeTab === 'notifications' && (
          <div className="lg:hidden">
            <NotificationCenter />
          </div>
        )}
      </div>

      {/* Desktop Notifications Panel */}
      <div className="hidden lg:block lg:w-1/4 border-l border-white/10 bg-white/5">
        <NotificationCenter />
      </div>
    </div>
  );
};

export default MessageCenter;